<template>
    <section>
        <div class="card">
            <div style="flex-grow: 1;">
                <div class="card-row" v-if="$store.getters.getLocalAddressbookName(address) || addressMeta?.name">
                    <div class="card-row__name" v-text="$t('address.info.name')"/>
                    <div class="card-row__value">
                        <div>
                            <span>{{ $store.getters.getLocalAddressbookName(address) || addressMeta?.name }}</span>
                        </div>
                    </div>
                </div>

                <div class="card-row">
                    <div class="card-row__name" v-text="$t('common.address')" />
                    <div class="card-row__value">
                        <ui-copy-button v-bind:copy="address" v-bind:successMessage="$t('address.info.copy_success')">
                            {{address}}
                        </ui-copy-button>

                        <span class="card-main-qr-button" v-on:click="qrModalVisible = true">
                            <icon-qr class="card-main-qr-button__icon"/>
                        </span>
                    </div>
                </div>

                <div class="card-row">
                    <div class="card-row__name" v-text="$t('address.info.balance')"/>
                    <div class="card-row__value" v-if="balance == '0' || balance">
                        {{$ton(balance)}} TON

                        <template v-if="balance != '0'">
                            <span v-if="$store.state.exchangeRate" style="color: #717579">
                                ≈ <ui-fiat v-bind:tonValue="balance"/>
                            </span>
                        </template>

                        <!--<span class="muted">&bull;</span>-->
                        <!--<a v-bind:href="vestingLink" target="_blank" v-text="$t('vesting.link')"/>-->
                    </div>
                    <div v-else class="card-row__value">
                        <span class="skeleton">00000 TON ≈ 00000 USD</span>
                    </div>
                </div>
                <div class="card-row">
                    <div class="card-row__name" v-text="$t('multisig.threshold')"/>
                    <div class="card-row__value">
                        <span v-if="threshold && signers.length ">
                            {{ threshold }} {{$t('common.of')}} {{ signers.length }}
                        </span>
                        <span v-else class="skeleton">1 / 2 signers</span>
                    </div>
                </div>
                <div class="card-row">
                    <div class="card-row__name" v-text="$t('multisig.next_order_id')"/>
                    <div class="card-row__value">
                        <span v-if="orderId && orderId === '-1'">
                            {{ $t('multisig.arbitrary') }}
                        </span>
                        <span v-else-if="orderId && orderId !== '-1'">
                            {{ orderId }}
                        </span>
                        <span v-else class="skeleton">1 / 2 signers</span>
                    </div>
                </div>

                <div class="card-row">
                    <div class="card-row__name" v-text="$t('address.info.details')"/>
                    <div class="card-row__value">
                        <div class="card-row__value-tags">
                            <div
                                class="card-row__value-tag-item"
                                v-on:mouseover="showTooltip.state = true"
                                v-on:mouseleave="showTooltip.state = false"
                            >
                                <div v-if="wallet?.status === undefined" class="card-row__value-tag card-row__value-skeleton skeleton">
                                    <span>NNNNN</span>
                                </div>
                                <div v-else-if="wallet?.status === 'active'" class="card-row__value-tag card-row__value-tag-green">
                                    <span>{{ $t('address.info.type_active') }}</span>
                                </div>
                                <div v-else-if="wallet?.status === 'frozen'" class="card-row__value-tag card-row__value-tag-blue">
                                    <span>{{ $t('address.info.type_frozen') }}</span>
                                </div>
                                <ui-link v-bind:to="{name: 'suspended'}" v-else-if="isSuspended" class="card-row__value-tag card-row__value-tag-orange router-link__no-style">
                                    <span>{{ $t('address.info.type_suspended') }}</span>
                                </ui-link>
                                <div v-else class="card-row__value-tag card-row__value-tag-gray">
                                    <span>{{ $t('address.info.type_inactive') }}</span>
                                </div>
                                <ui-tooltip
                                    v-bind:value="$t('address.info.state')"
                                    position="top"
                                    v-if="showTooltip.state"
                                />
                            </div>
                            <div
                                v-if="contractTypeVisible && !isUninit"
                                class="card-row__value-tag-item"
                                v-on:mouseover="showTooltip.contract = true"
                                v-on:mouseleave="showTooltip.contract = false"
                            >
                                <div v-if="!contractType" class="card-row__value-tag card-row__value-skeleton skeleton">
                                    <span>NNNNN</span>
                                </div>
                                <div v-else-if="contractType"
                                    class="card-row__value-tag card-row__value-tag-gray"
                                >
                                    {{ contractTypeFormatted }}
                                </div>
                                <ui-tooltip
                                    v-bind:value="$t('address.info.contract_type')"
                                    position="top"
                                    v-if="showTooltip.contract"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ui-modal class="qr-modal" v-bind:isOpen.sync="qrModalVisible">
            <ui-qr show-logo v-bind:value="`ton://transfer/${address}`" v-bind:size="300"/>
        </ui-modal>

        <multisig-tabs style="margin-top: 18px;"
            v-bind:address="address"
            v-bind:signers="signers"
            v-bind:proposers="proposers"
            v-bind:addressbook="addressbook"
            v-bind:orders="orders"
            v-bind:is-loading="isLoading"
            v-bind:is-active="wallet?.status === 'active'"
        >
            <template v-slot:customCalendar>
                <custom-calendar v-if="$store.state.filterShow" />
            </template>
            <template v-slot:customFilter>
                <custom-filter v-if="$store.state.filterShow" />
            </template>
        </multisig-tabs>
    </section>
</template>

<script>
import IconQr from '@img/icons/tonscan/qr-14.svg?vue';
import UiQr from '~/components/UiQr.vue';
import CustomFilter from '~/components/address/Filter/Filter.vue';
import { getAccountStates, getMultisig } from '~/api/toncenterV3';
import CustomCalendar from '~/components/address/Calendar/Calendar.vue';
import MultisigTabs from './MultisigTabs.vue';
import contractHashes from '../../../json/contractHashes.json';

export default {
    props: {
        address: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            threshold: undefined,
            signers: [],
            proposers: [],
            balance: undefined,
            orderId: undefined,
            allowArbitraryOrderSeqno: false,
            showTooltip: {
                state: false,
                contract: false,
            },
            qrModalVisible: false,
            wallet: {},
            contractType: undefined,
            contractTypeVisible: true,
            isUninit: false,
            isLoading: true,
            addressbook: undefined,
            orders: undefined,
        };
    },

    computed: {
        addressMeta() {
            return this.$store.getters.getAddressMeta(this.address);
        },
        contractTypeFormatted() {
            if (this.contractType.startsWith('wallet')) {
                return this.contractType.replaceAll('_', ' ');
            }

            return this.$t(`address.contract_type.${this.contractType}`);
        },
    },

    created() {
        this.loadData();
    },

    methods: {
        async loadData() {
            const response = await getAccountStates(this.address);
            this.wallet = response?.accounts[0];
            this.balance = this.wallet.balance;
            this.contractType = contractHashes[this.wallet.code_hash];

            const multisigToncenter = await getMultisig(this.address);
            const multisig = multisigToncenter.multisigs[0];
            this.addressbook = multisigToncenter.address_book;

            this.threshold = multisig.threshold;
            this.signers = multisig.signers;
            this.proposers = multisig.proposers;
            this.orderId = multisig.next_order_seqno;
            this.orders = multisig.orders.sort((a, b) => b.expiration_date > a.expiration_date);
            this.isLoading = false;

            this.presaveUserAddressbook();
        },

        presaveUserAddressbook() {
            const payload = {
                address: this.address,
                name: this.addressMeta?.name,
                type: 'address',
            };
            this.$store.dispatch('presaveAddressbookItem', payload);
        },
    },

    components: {
        CustomCalendar,
        CustomFilter,
        MultisigTabs,
        // IconTitleHelp,
        UiQr,
        IconQr,
    },
};
</script>
